import { Copy, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

export const PromptLyricsPanel = ({ analysis }) => {
  const copy = async (text, msg = "Copied to clipboard") => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      if (!ok) return toast.error("Copy failed");
    }
    toast.success(msg);
  };

  return (
    <div className="border border-white/10 bg-zinc-900/40 rounded-sm" data-testid="prompt-lyrics-panel">
      <Tabs defaultValue="prompt">
        <div className="border-b border-white/10 px-2">
          <TabsList className="bg-transparent p-0 h-auto gap-0">
            <TabsTrigger
              value="prompt"
              data-testid="tab-prompt"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-emerald-500 data-[state=active]:border-b-2 data-[state=active]:border-emerald-500 border-b-2 border-transparent font-mono-display text-xs tracking-[0.2em] uppercase py-4 px-6 text-zinc-500"
            >
              Suno Prompt
            </TabsTrigger>
            <TabsTrigger
              value="lyrics"
              data-testid="tab-lyrics"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-fuchsia-400 data-[state=active]:border-b-2 data-[state=active]:border-fuchsia-400 border-b-2 border-transparent font-mono-display text-xs tracking-[0.2em] uppercase py-4 px-6 text-zinc-500"
            >
              Generated Lyrics
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="prompt" className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              suno.ai // ready-to-paste
            </span>
            <Button
              onClick={() => copy(analysis.suno_prompt, "Suno prompt copied")}
              data-testid="copy-prompt-btn"
              variant="outline"
              className="rounded-none border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10 font-mono-display text-[10px] tracking-[0.25em] uppercase h-8"
            >
              <Copy className="w-3 h-3 mr-2" /> Copy
            </Button>
          </div>
          <div
            data-testid="suno-prompt-text"
            className="font-mono-display text-base leading-relaxed text-white bg-black/40 border border-emerald-500/20 p-6 rounded-sm break-words whitespace-pre-wrap min-h-[120px]"
          >
            {analysis.suno_prompt}
          </div>
          <div className="mt-4 flex items-center gap-2 font-mono-display text-[10px] tracking-[0.2em] uppercase text-zinc-500">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flicker" />
            Paste into Suno's "Custom" mode → Style of Music field.
          </div>
        </TabsContent>

        <TabsContent value="lyrics" className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <span
              data-testid="lyrics-rating-badge"
              className={`font-mono-display text-[10px] tracking-[0.25em] uppercase px-3 py-2 border ${
                analysis.lyrics_rating === "explicit" ? "border-fuchsia-400/50 text-fuchsia-400" : "border-emerald-500/50 text-emerald-500"
              }`}
            >
              {analysis.lyrics_rating === "explicit" ? "Explicit 18+" : "Clean"}
            </span>
            {analysis.lyrics && (
              <Button
                onClick={() => copy(analysis.lyrics, "Lyrics copied")}
                data-testid="copy-lyrics-btn"
                variant="outline"
                className="rounded-none border-fuchsia-400/50 text-fuchsia-400 hover:bg-fuchsia-400/10 font-mono-display text-[10px] tracking-[0.25em] uppercase h-10"
              >
                <Copy className="w-3 h-3 mr-2" /> Copy
              </Button>
            )}
          </div>

          {analysis.lyrics ? (
            <ScrollArea className="h-[380px] border border-fuchsia-400/20 bg-black/40 rounded-sm">
              <pre
                data-testid="lyrics-text"
                className="font-mono-display text-sm leading-relaxed text-white whitespace-pre-wrap break-words p-6"
              >
                {analysis.lyrics}
              </pre>
            </ScrollArea>
          ) : (
            <div className="border border-dashed border-white/10 p-10 text-center rounded-sm">
              <Sparkles className="w-6 h-6 mx-auto text-fuchsia-400 mb-3 opacity-60" />
              <div className="font-mono-display text-sm text-zinc-400">
                No lyrics were saved for this older decode.
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
